import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { AuthContext } from '../../../context/auth-context';
import ProductionMetricsAPI from '../../../../services/api/analytics/production-metrics-api';
import PackageTableRow from './PackageTableRow';
import WorkPackageProgressModal from './workPackageProgressModal/WorkPackageProgressModal';

// ── Column header definitions ────────────────────────────────────────────

const COLUMNS = [
  { label: 'Package', align: 'left', width: undefined },
  { label: 'PM', align: 'left', width: 110 },
  { label: 'Schedule %', align: 'right', width: 80 },
  { label: 'Actual %', align: 'right', width: 72 },
  { label: '% Earned', align: 'right', width: 80 },
  { label: '', align: 'center', width: 32 }, // ⚑ over-budget flag
  { label: 'Planned Dates', align: 'left', width: 150 },
  { label: 'Schedule', align: 'center', width: 80 },
  { label: 'Budget Hrs', align: 'right', width: 100 },
  { label: 'Actual Hrs', align: 'right', width: 90 },
  { label: 'Earned Hrs', align: 'right', width: 90 },
  { label: 'Last Updated', align: 'left', width: 140 },
  { label: '', align: 'right', width: 130 }, // Action
];

const headerCellSx = {
  fontSize: '10px',
  fontWeight: 600,
  color: 'text.secondary',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  py: 0.75,
  whiteSpace: 'nowrap',
  borderBottom: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  position: 'sticky',
  top: 0,
  zIndex: 3,
};

// Keeps the "Update Progress" action column pinned to the right edge so it stays
// reachable on narrow / laptop screens where the wide table scrolls horizontally.
const stickyActionHeaderSx = {
  right: 0,
  zIndex: 4,
  borderLeft: '0.5px solid',
  borderLeftColor: 'divider',
};

// ── Component ────────────────────────────────────────────────────────────

/**
 * MUI dense table for the Work Package Progress tab. Reused by all six section
 * groups (pre, rdy, shop, field, cmp).
 * Renders sticky column headers, alternating row stripes, and a bounded
 * scroll area on TableContainer (required for MUI stickyHeader to work).
 *
 * Props
 * ─────
 * packages     Array   package objects from the GET /packages response
 * pctKey       string  'fieldPercentComplete' | 'shopPercentComplete'
 * budgetKey    string  'fieldBudgetHrs'       | 'shopBudgetHrs'
 * actualKey    string  'fieldActualHrs'       | 'shopActualHrs'
 * updatedAtKey string  'fieldPctUpdatedAt'    | 'shopPctUpdatedAt'
 * updatedByKey string  'fieldPctUpdatedBy'    | 'shopPctUpdatedBy'
 * patchSource  string  'field' | 'shop'
 * isField      bool    true → field context (modal copy and prefab budget flow)
 */
const PackageTable = ({
  packages,
  pctKey,
  budgetKey,
  actualKey,
  updatedAtKey,
  updatedByKey,
  patchSource,
  isField,
}) => {
  const auth = useContext(AuthContext);

  // Local copy for optimistic updates after saves
  const [localPkgs, setLocalPkgs] = useState(packages || []);
  useEffect(() => {
    setLocalPkgs(packages || []);
  }, [packages]);

  const [selectedPkgId, setSelectedPkgId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // ── Modal open / close ─────────────────────────────────────────────────

  const openPackageDialog = (pkg) => {
    setSelectedPkgId(pkg.id);
    setError(null);
  };

  const closePackageDialog = () => {
    if (saving) return;
    setSelectedPkgId(null);
    setError(null);
  };

  const handleBudgetAllocationConfirm = async (
    pkgId,
    { newShopBudgetHrs, newFieldBudgetHrs, notes }
  ) => {
    setSaving(true);
    setError(null);
    try {
      const budgetResult = await ProductionMetricsAPI.updateBudgetSplit(
        pkgId,
        { newShopBudgetHrs, newFieldBudgetHrs, notes },
        auth.token
      );
      setLocalPkgs((prev) =>
        prev.map((p) =>
          p.id === pkgId
            ? {
                ...p,
                shopBudgetHrs: budgetResult.shopBudgetHrs,
                fieldBudgetHrs: budgetResult.fieldBudgetHrs,
              }
            : p
        )
      );
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Save failed');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // ── Save handler (PATCH percent-complete + optional note only; budget split uses confirm in modal) ─

  const handleModalSave = async (
    pkgId,
    {
      fieldPercentComplete,
      shopPercentComplete,
      notes,
      saveFieldPercent,
      saveShopPercent,
    }
  ) => {
    setSaving(true);
    setError(null);
    try {
      const patch = {};

      if (saveFieldPercent) {
        const pctResult = await ProductionMetricsAPI.updatePercentComplete(
          pkgId,
          {
            pctType: 'field',
            percentComplete: fieldPercentComplete,
            source: 'metrics_dashboard',
            notes,
          },
          auth.token
        );

        patch.fieldPercentComplete = pctResult.fieldPercentComplete;
        patch.fieldPctUpdatedAt = pctResult.fieldPctUpdatedAt;
        patch.fieldPctUpdatedBy = pctResult.updatedBy;
      }

      if (saveShopPercent) {
        const pctResult = await ProductionMetricsAPI.updatePercentComplete(
          pkgId,
          {
            pctType: 'shop',
            percentComplete: shopPercentComplete,
            source: 'metrics_dashboard',
            notes,
          },
          auth.token
        );

        patch.shopPercentComplete = pctResult.shopPercentComplete;
        patch.shopPctUpdatedAt = pctResult.shopPctUpdatedAt;
        patch.shopPctUpdatedBy = pctResult.updatedBy;
      }

      setLocalPkgs((prev) =>
        prev.map((p) =>
          p.id === pkgId
            ? {
                ...p,
                ...patch,
              }
            : p
        )
      );

      setSelectedPkgId(null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────

  if (!localPkgs.length) return null;

  const selectedPkg = localPkgs.find((pkg) => pkg.id === selectedPkgId) || null;

  return (
    <Box>
      <TableContainer
        sx={{
          maxHeight: 480,
          overflow: 'auto',
        }}
      >
        <Table size="small" stickyHeader>
          {/* ── Column headers ──────────────────────────────────── */}
          <TableHead>
            <TableRow>
              {COLUMNS.map((col, i) => (
                <TableCell
                  key={i}
                  align={col.align}
                  sx={{
                    ...headerCellSx,
                    ...(col.width
                      ? { width: col.width, minWidth: col.width }
                      : {}),
                    ...(i === COLUMNS.length - 1 ? stickyActionHeaderSx : {}),
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* ── Package rows ────────────────────────────────────── */}
          <TableBody>
            {localPkgs.map((pkg, idx) => (
              <PackageTableRow
                key={pkg.id}
                pkg={pkg}
                onUpdateClick={openPackageDialog}
                isEvenRow={idx % 2 === 1}
                isField={isField}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <WorkPackageProgressModal
        open={Boolean(selectedPkg)}
        pkg={selectedPkg}
        isField={isField}
        saving={saving}
        error={error}
        onClose={closePackageDialog}
        onSave={handleModalSave}
        onBudgetAllocationConfirm={handleBudgetAllocationConfirm}
      />
    </Box>
  );
};

export default PackageTable;
